<script setup lang="ts">
import { ref, computed } from 'vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import MercariProductList from '@/components/MercariProductList.vue'
import BaseTabs from '@/components/ui/baseTab/BaseTabs.vue'
import BaseTabItem from '@/components/ui/baseTab/BaseTabItem.vue'
import { getMercariUrl } from '@/lib/mercari'
import type {
	IMercariProductListFetchCompleteEvent,
	IMercariProductListItemClickEvent,
} from '@/components/MercariProductList.vue'
import type {
	ShopIdProductListTab,
	BuyaholicProductListTab,
} from './_MercariCampaign.vue'
import {
	buildMercariRedirectParams,
	formatTrackingKeyword,
} from '@/types/Mercari'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'

const BUYAHOLIC_TAB_TYPES = [
	'buyaholic-filter',
	'buyaholic-search',
	'buyaholic-user',
] as const
type BuyaholicTabType = (typeof BUYAHOLIC_TAB_TYPES)[number]

const isBuyaholicTab = (
	tab: ShopIdProductListTab,
): tab is BuyaholicProductListTab => {
	return BUYAHOLIC_TAB_TYPES.includes(tab.type as BuyaholicTabType)
}
const redirectUrlGenerators: Record<
	BuyaholicTabType,
	(tab: BuyaholicProductListTab) => string
> = {
	'buyaholic-filter': (tab: BuyaholicProductListTab) => {
		return `${getSiteBaseUrl()}/sharing/discover/recommended`
	},

	'buyaholic-search': (tab: BuyaholicProductListTab) => {
		const keyword = tab.keyword || ''
		return `${getSiteBaseUrl()}/sharing/search/?q=${encodeURIComponent(
			keyword,
		)}`
	},

	'buyaholic-user': (tab: BuyaholicProductListTab) => {
		const username = tab.username || ''
		return `${getSiteBaseUrl()}/sharing/users/${encodeURIComponent(username)}`
	},
}

const useGATracking = () => {
	const trackTabClick = (tab: ShopIdProductListTab) => {
		const isTabBuyaholic = BUYAHOLIC_TAB_TYPES.includes(
			tab.type as BuyaholicTabType,
		)

		if (isTabBuyaholic) {
			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: 'Campaign Page',
				button_name: 'Community Post Listing Tab',
				event_extra: tab.label,
			})
		} else {
			const eventExtra = tab.shop_id
				? `shop_id_${tab.shop_id}`
				: `keyword_${tab.keyword}`

			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: GaGeneralPageName.MercariCampaign,
				button_name: 'Product Listing Tab',
				event_extra: eventExtra,
			})
		}
	}

	const trackViewMore = (
		isShopMode: boolean,
		shopId: string,
		keyword: string,
		isBuyaholicMode: boolean = false,
	) => {
		if (isBuyaholicMode) {
			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: GaGeneralPageName.MercariCampaign,
				button_name: 'View More Community Post Listing',
			})
		} else {
			const eventExtra = isShopMode ? `shop_${shopId}` : `keyword_${keyword}`

			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: GaGeneralPageName.MercariCampaign,
				button_name: 'ViewMore_ProductList',
				event_extra: eventExtra,
			})
		}
	}

	const trackItemClick = (
		event: IMercariProductListItemClickEvent,
		isShopMode: boolean,
		shopId: string,
		keyword: string,
		isBuyaholicMode: boolean = false,
		currentTab?: BuyaholicProductListTab,
	) => {
		if (isBuyaholicMode && currentTab) {
			createGAEvent('select_item', {
				page_name: GaGeneralPageName.MercariCampaign,
				items: [
					{
						location_id: GaGeneralPageName.MercariCampaign,
						item_list_name: 'Community Post Listing',
						item_id: `post_${event.item.id}`,
						item_name: event.item.name,
						item_category: 'Normal Request',
						index: event.index + 1,
						sort_option: currentTab.sort || 'Recommended',
						warehouse_filter: currentTab.warehouses || 'all',
						category_filter: currentTab.interests || 'all',
						keyword: keyword || '',
					},
				],
			})
		} else {
			const trackingKeyword = formatTrackingKeyword(isShopMode, shopId, keyword)

			createGAEvent('select_item', {
				page_name: GaGeneralPageName.MercariCampaign,
				items: [
					{
						location_id: GaGeneralPageName.MercariCampaign,
						item_list_name: 'Product Listing-Campaign',
						item_id: `product_${event.item.id}`,
						item_name: event.item.name,
						item_category: 'Mercari',
						index: event.index + 1,
						keyword: trackingKeyword,
					},
				],
			})
		}
	}

	return {
		trackTabClick,
		trackViewMore,
		trackItemClick,
	}
}

const props = defineProps<{
	title: string
	subtitle: string
	tabs: (ShopIdProductListTab | BuyaholicProductListTab)[]
	hasError?: boolean
	pageContent: any
}>()

const emit = defineEmits<{
	'fetch-complete': [event: IMercariProductListFetchCompleteEvent]
}>()

const gaTracking = useGATracking()

const productListRef = ref<InstanceType<typeof MercariProductList> | null>(null)

const currentTabIndex = ref(0)

const formattedKeywords = computed(() => {
	if (!props.tabs?.length) return []
	return props.tabs.map(tab => tab.label)
})

const currentTab = computed<ShopIdProductListTab | BuyaholicProductListTab>(
	() => {
		if (props.tabs && props.tabs.length > currentTabIndex.value) {
			const tab = { ...props.tabs[currentTabIndex.value] }
			return tab
		}

		return {
			type: 'mercari',
			label: '',
			shop_id: '',
			keyword: '',
			warehouses: '',
			interests: '',
			sort: '',
			username: '',
		} as BuyaholicProductListTab
	},
)

const isCurrentTabBuyaholic = computed(() => isBuyaholicTab(currentTab.value))

const currentKeyword = computed(() => {
	return currentTab.value.keyword || currentTab.value.label
})

const currentShopId = computed(() => {
	return currentTab.value.shop_id
})

const isShopMode = computed(() => {
	return !!currentTab.value.shop_id
})

const handleKeywordClick = (label: string, index: number) => {
	if (currentTabIndex.value !== index) {
		currentTabIndex.value = index
	}

	if (!props.tabs || index >= props.tabs.length) return

	gaTracking.trackTabClick(props.tabs[index])
}

const redirectToTargetPage = (keyword?: string) => {
	if (isCurrentTabBuyaholic.value) {
		const tab = currentTab.value as BuyaholicProductListTab

		const tabType = tab.type as BuyaholicTabType

		if (tabType in redirectUrlGenerators) {
			try {
				const redirectUrl = redirectUrlGenerators[tabType](tab)
				const trackingKeyword = tab.keyword || tab.label || tab.username || ''

				gaTracking.trackViewMore(false, '', trackingKeyword, true)

				window.location.href = redirectUrl
				return
			} catch (error) {
				console.error('Redirect URL have', error)
			}
		}
	}

	if (keyword && props.tabs?.length) {
		const shopTab = props.tabs.find(tab => tab.shop_id === keyword)
		if (shopTab) {
			const redirectParams = buildMercariRedirectParams(
				keyword, // shop_id
				'', // keyword
				shopTab.label, // shop_name
			)
			const redirectUrl = getMercariUrl('search', redirectParams)

			gaTracking.trackViewMore(true, keyword, shopTab.label, false)

			window.location.href = redirectUrl
			return
		}
	}

	const searchKeyword = keyword || currentKeyword.value

	const redirectParams = buildMercariRedirectParams(
		isShopMode.value ? currentShopId.value : undefined,
		isShopMode.value ? '' : searchKeyword,
		isShopMode.value ? currentTab.value.label : undefined,
	)

	const redirectUrl = getMercariUrl('search', redirectParams)

	let trackingKeyword = ''
	if (isShopMode.value) {
		trackingKeyword = currentShopId.value
	} else if (keyword && keyword.trim()) {
		trackingKeyword = keyword
	} else {
		trackingKeyword = currentKeyword.value
	}

	gaTracking.trackViewMore(
		isShopMode.value,
		currentShopId.value,
		trackingKeyword,
		isCurrentTabBuyaholic.value,
	)

	window.location.href = redirectUrl
}

const handleFetchComplete = (event: IMercariProductListFetchCompleteEvent) => {
	emit('fetch-complete', event)
}

const handleItemClick = (event: IMercariProductListItemClickEvent) => {
	handleProductNavigation(event)

	gaTracking.trackItemClick(
		event,
		isShopMode.value,
		currentShopId.value,
		currentKeyword.value,
		isCurrentTabBuyaholic.value,
		isCurrentTabBuyaholic.value
			? (currentTab.value as BuyaholicProductListTab)
			: undefined,
	)
}

const handleProductNavigation = (event: IMercariProductListItemClickEvent) => {
	const params = new URLSearchParams(window.location.search)
	const slug = params.get('slug')

	if (slug) {
		const targetUrl = new URL(event.targetUrl, window.location.origin)
		targetUrl.searchParams.set('source', slug)
		window.history.replaceState(
			{},
			'',
			`${window.location.pathname}?${params.toString()}`,
		)

		window.open(targetUrl.href, '_blank')
	} else {
		window.open(event.targetUrl, '_blank')
	}
}

defineExpose({
	productListRef,
})
</script>

<template>
	<div class="flex flex-col justify-center gap-casual-friends">
		<template v-if="!hasError">
			<div class="flex flex-col gap-strong-friends">
				<h2 class="typography-h3-str">
					{{ title }}
				</h2>
				<h4 class="typography-s1-reg">
					{{ subtitle }}
				</h4>
			</div>

			<div class="flex justify-center">
				<div class="no-scrollbar w-full max-w-full overflow-x-auto">
					<ul class="flex min-w-min justify-center">
						<BaseTabs size="medium" type="secondary">
							<BaseTabItem
								v-for="(tabItem, index) of formattedKeywords"
								:key="tabItem"
								type="secondary"
								size="medium"
								@click="handleKeywordClick(tabItem, index)"
								:isSelected="index === currentTabIndex"
							>
								{{ tabItem }}
							</BaseTabItem>
						</BaseTabs>
					</ul>
				</div>
			</div>
		</template>

		<MercariProductList
			ref="productListRef"
			:key="`${currentKeyword}-${currentShopId}`"
			:keyword="isShopMode ? '' : currentKeyword"
			:shopId="currentShopId"
			:location-id="GaGeneralPageName.MercariCampaign"
			:viewMoreMode="'redirect'"
			:item-content="props.pageContent.productItem"
			:edge-case="props.pageContent.edgeCase"
			:load-more-button-text="props.pageContent.productListLoadMoreButtonText"
			:type="isCurrentTabBuyaholic ? currentTab.type : 'mercari'"
			:warehouses="
				isCurrentTabBuyaholic
					? (currentTab as BuyaholicProductListTab).warehouses || ''
					: ''
			"
			:interests="
				isCurrentTabBuyaholic
					? (currentTab as BuyaholicProductListTab).interests || ''
					: ''
			"
			:sort="
				isCurrentTabBuyaholic
					? (currentTab as BuyaholicProductListTab).sort || ''
					: ''
			"
			:username="
				isCurrentTabBuyaholic
					? (currentTab as BuyaholicProductListTab).username || ''
					: ''
			"
			@fetch-complete="handleFetchComplete"
			@item-click="handleItemClick"
			@view-more-click="redirectToTargetPage"
		/>
	</div>
</template>
